import { useState } from "react";
import { Menu, ChevronDown, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useNavigate, useLocation } from "react-router-dom";
import acLogo from "@/assets/ac-logo-small.webp";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (id: string) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        if (id === "#home") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
      return;
    }
    if (id === "#home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const navTo = (path: string) => {
    setOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navLinkClass = "text-background hover:text-primary transition-colors text-lg cursor-pointer";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b border-border py-3 bg-card-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <button
            onClick={() => navTo("/")}
            className="flex items-center gap-2"
          >
            <img src={acLogo} alt="ATC Logo" className="h-10 w-10 object-cover shadow-none" />
            <span className="text-foreground font-bold text-lg hidden sm:block">ATC</span>
          </button>

          {/* Desktop Navigation Links (Hidden on small screens) */}
          <div className="hidden md:flex items-center gap-6">
            <div className="relative group">
              <button onClick={() => scrollTo("#home")} className={`${navLinkClass} flex items-center gap-1`}>
                Home <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute top-full left-0 pt-1 hidden group-hover:block z-50">
                <div className="bg-popover border border-border rounded-md shadow-md py-1 min-w-[140px]">
                  <button onClick={() => scrollTo("#about")} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors">About</button>
                  <button onClick={() => scrollTo("#faq")} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors">FAQ</button>
                  <button onClick={() => scrollTo("#services")} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors">Services</button>
                </div>
              </div>
            </div>

            <button onClick={() => navTo("/pricing")} className={navLinkClass}>
              Pricing
            </button>
            <button onClick={() => navTo("/update-file")} className={navLinkClass}>
              Update File
            </button>
            <button onClick={() => navTo("/contact")} className={navLinkClass}>
              Contact
            </button>
          </div>

          {/* CTA Button, Admin Login, and Mobile Menu Toggle */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Button onClick={() => navTo("/get-started")} className="hidden sm:inline-flex text-sm" size="sm">
              Get Started!
            </Button>

            {/* Admin Login Icon (Desktop & Tablet) */}
            <Button
              variant="ghost"
              size="icon"
              className="hidden sm:flex text-muted-foreground hover:text-primary"
              onClick={() => navTo("/admin")}
              title="Admin Login"
            >
              <User className="h-5 w-5" />
            </Button>

            {/* Mobile menu (Hamburger) */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-border w-72 flex flex-col">
                <SheetTitle className="text-foreground">Menu</SheetTitle>

                <div className="flex flex-col gap-4 mt-8 flex-1">
                  <button
                    onClick={() => scrollTo("#home")}
                    className="text-foreground hover:text-primary transition-colors text-left text-base py-2 font-medium"
                  >
                    Home
                  </button>
                  <div className="flex flex-col gap-2 pl-4">
                    <button
                      onClick={() => scrollTo("#about")}
                      className="text-muted-foreground hover:text-primary transition-colors text-left text-sm py-1"
                    >
                      About
                    </button>
                    <button
                      onClick={() => scrollTo("#faq")}
                      className="text-muted-foreground hover:text-primary transition-colors text-left text-sm py-1"
                    >
                      FAQ
                    </button>
                    <button
                      onClick={() => scrollTo("#services")}
                      className="text-muted-foreground hover:text-primary transition-colors text-left text-sm py-1"
                    >
                      Services
                    </button>
                  </div>
                  <button
                    onClick={() => navTo("/pricing")}
                    className="text-muted-foreground hover:text-primary transition-colors text-left text-base py-2"
                  >
                    Pricing
                  </button>
                  <button
                    onClick={() => navTo("/update-file")}
                    className="text-muted-foreground hover:text-primary transition-colors text-left text-base py-2"
                  >
                    Update File
                  </button>
                  <button
                    onClick={() => navTo("/contact")}
                    className="text-muted-foreground hover:text-primary transition-colors text-left text-base py-2"
                  >
                    Contact
                  </button>
                  <Button onClick={() => navTo("/get-started")} className="mt-4">
                    Get Started!
                  </Button>
                </div>

                {/* Admin Login Link at the bottom of the mobile menu */}
                <div className="pt-6 mt-auto border-t border-border">
                  <button
                    onClick={() => navTo("/admin")}
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors w-full text-left text-sm"
                  >
                    <User className="h-4 w-4" /> Admin Portal
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
